import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { UserRole } from '../../../../../types';
import { useForm, Controller } from 'react-hook-form';

import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { UserKeys, updateRoleSettings } from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import AvatarChip from '../../../../shared/avatar-chip';
import { Messages } from '../../../../utils/message-constants';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface RoleSettingsFormProps {
  changeLogRoles: string[];
  role: UserRole;
}

export default function RoleSettingsForm({
  changeLogRoles,
  role,
}: RoleSettingsFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const queryClient = useQueryClient();

  const { mutate, isPending: updatingRole } = useMutation({
    mutationFn: (data: UserRole) => updateRoleSettings(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Role settings'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) {
          return;
        } else {
          const filteredList = oldData.filter(
            (currentRole) => currentRole.roleName !== role.roleName
          );
          return [...filteredList, data];
        }
      });
      reset();
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('the role settings'),
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(analytics.administration.role_details.update_role_settings, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: data.roleType,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading },
    getValues,
    setValue,
    watch,
    reset,
  } = useForm<UserRole>({
    values: {
      ...role,
      notificationUsers: role.notificationUsers ? role.notificationUsers : [],
    },
  });

  const onSubmit = (data: UserRole) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, AdministrationRoles.EditRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const notificationUsers = watch('notificationUsers') || [];

  return (
    <Box
      component="form"
      sx={{ position: 'relative', height: '100%' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{ position: 'absolute', top: -50, right: -15 }}
      >
        <Button
          variant="contained"
          disabled={!isDirty || updatingRole}
          loading={updatingRole || isLoading}
          sx={{ position: 'absolute', top: 0, right: 0 }}
          type="submit"
        >
          Save
        </Button>
      </Stack>

      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Description
        </Typography>
        <Box sx={{ pt: 2, px: 4 }}>
          <Controller
            name={'desc'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                slotProps={{
                  input: {
                    id: 'desc',
                  },
                  inputLabel: {
                    htmlFor: 'desc',
                  },
                }}
                label="Role Description"
                disabled={role.default}
                autoComplete="off"
                multiline
                sx={{ width: 400 }}
              />
            )}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Change Log Settings
        </Typography>
        <Stack sx={{ pt: 1, px: 4 }} gap={2}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            The minimum length of a change log that an admin must write when
            making a change.
          </Typography>
          <Controller
            name="changeLogLength"
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value || 0}
                inputProps={{
                  id: 'changeLogLength',
                  min: 0,
                }}
                InputLabelProps={{
                  htmlFor: 'changeLogLength',
                }}
                type={'number'}
                label="Change Log Length"
                onChange={field.onChange}
                sx={{ width: 400 }}
                autoComplete="off"
              />
            )}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Password Settings
        </Typography>
        <Stack
          sx={{
            pt: 1,
            px: 4,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            If Enabled, Role users can see the passwords instead of the masked
            password box.
          </Typography>
          <Controller
            name="canSeePasswords"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Show Passwords"
              />
            )}
          />
        </Stack>
        <Divider sx={{ mt: 1 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Notification Settings
        </Typography>
        <Stack
          sx={{
            pt: 1,
            px: 4,
          }}
          gap={1}
        >
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Optionally select users or groups who will be notified by email
            whenever an action whose permission has "Immediate Notifications"
            selected is taken in gPanel.
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteResponse}
                setAutoCompleteValue={setAutoCompleteResponse}
                onSelection={(value) => {
                  if (
                    !value ||
                    (notificationUsers && notificationUsers.includes(value))
                  )
                    return;
                  const updatedUsers = [...notificationUsers, value];
                  setValue('notificationUsers', updatedUsers, {
                    shouldDirty: true,
                  });
                  // autocompleteKey.current = new Date().getTime().toString();
                  setAutoCompleteResponse('');
                }}
              />
            </Box>
          </Stack>
          {notificationUsers && notificationUsers.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {notificationUsers.map((user) => {
                return (
                  <AvatarChip
                    key={user}
                    label={user}
                    type={'Unknown'}
                    email={user}
                    onDelete={() => {
                      setValue(
                        'notificationUsers',
                        notificationUsers.filter(
                          (notifiedUser) => notifiedUser !== user
                        ),
                        {
                          shouldDirty: true,
                        }
                      );
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newRole: UserRole = {
            ...getValues(),
          };
          mutate(newRole);
        }}
        mutateLoading={updatingRole}
      />
    </Box>
  );
}
